import Vue from 'vue'
import Laravel from '@/services/Laravel'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import Mtxs from '@/constants/Mutations'

import _get from 'lodash/get'
import _snakeCase from 'lodash/snakeCase'
import CustomerService from '@/services/CustomerService'
import CustomerModel from '@/models/CustomerModel'
import OrderModel from '@/models/OrderModel'

export const state = {
  customer: {},
  customers: [],
  cache: {},
} // state

export const mutations = {
  [Mtxs.CACHE_CUSTOMER](state, { id, customer }) {
    Vue.set(state.cache, id, customer)
  },
  [Mtxs.SET_CUSTOMER](state, customer) {
    state.customer = customer
  },

  [Mtxs.UPDATE_CUSTOMER](state, data) {
    Vue.set(state, 'customer', { ...state.customer, ...data })
  },
  [Mtxs.SET_CUSTOMERS](state, customers) {
    state.customers = customers
  },
} // mutations

export const getters = {
  // Customer
  getCachedCustomer: (state) => (id) => _get(state.cache, id, false),
  getCustomerByPhone: (_, getters) => (phone) => {
    let phoneId = formatPhoneNumber(phone)
    return getters.getCachedCustomer(phoneId)
  },
  getCustomer: (state) => state.customer,
  getCustomerId: (state) => _get(state, 'customer.id'),
  getCustomerField: (state) => (key, defaultVal = undefined) =>
    _get(state, `customer['${_snakeCase(key)}']`, defaultVal),
  getCustomerPhone: (_, getters) => getters.getCustomerField('Phone', ''),
  getCustomerPhoneFormatted: (_, getters) =>
    formatPhoneNumber(getters.getCustomerPhone),
  getCustomerPhoneDigits: (_, getters) =>
    getters.getCustomerPhone.replace(/\D/g, ''),
  getCustomerOrderIds: (_, getters) => getters.getCustomerField('Orders', []),
  getCustomerAddress: (_, getters) => getters.getCustomerField('Address', ''),
  getCustomerName: (_, getters) =>
    `${getters.getCustomerFirstName} ${getters.getCustomerLastName}`.trim(),
  getCustomerFirstName: (_, getters) =>
    getters.getCustomerField('First Name', ''),
  getCustomerLastName: (_, getters) =>
    getters.getCustomerField('Last Name', ''),
  getCustomerEmail: (_, getters) => {
    let email = getters.getCustomerField('Email', false)
    return email ? email.trim() : false
  },
  getFamilyDiscount: (_, getters) =>
    getters.getCustomerField('Family Discount', 0),
  getCustomerDashboardLink: (_, getters, __, rootGetters) => {
    return `${rootGetters['shop/getShopRoot']}/customer/orders/`
  },
} // getters

export const actions = {
  async fetchCustomer({ dispatch, commit }, customerId) {
    let customer
    try {
      customer = await CustomerService.find(customerId)
      commit(Mtxs.SET_CUSTOMER, customer)
      dispatch('setupFullStory')
      return customer
    } catch (error) {
      if (!customer) {
        commit(Mtxs.SET_CUSTOMER, undefined)
        return false
      }
      throw Error(error)
    }
  }, // fetchCustomer

  async fetchCustomers() {
    try {
      let { response } = await CustomerModel.fetchForShop()
      return response
    } catch (error) {
      throw Error(error)
    }
  }, // fetchCustomers

  async sendCode(ctx, phone) {
    try {
      let wasCodeSent = await CustomerService.sendCode(phone)
      return wasCodeSent
    } catch (error) {
      return false
    }
  }, // sendUserCode

  async verifyPhone({ commit, dispatch }, { phone, code }) {
    try {
      let res = await CustomerService.verify({ phone, code })
      commit(`auth/${Mtxs.SET_TOKEN}`, res.token, { root: true })
      dispatch('auth/setLaravelAuthHeaders', null, { root: true })
      return res
    } catch (error) {
      return false
    }
  }, // verifyPhone

  async checkCustomerExists(ctx, phone) {
    try {
      let customerData = await CustomerService.exists(phone)
      return customerData
    } catch (error) {
      return false
    }
  }, // checkCustomerExists

  async createCustomer({ commit, dispatch, rootGetters }, userData) {
    try {
      let shopId = rootGetters['shop/getShopId']
      let { data: cust } = await Laravel.post(`/${shopId}/signup`, { userData })
      commit(Mtxs.SET_CUSTOMER, cust)
      dispatch('setupFullStory')
      return cust
    } catch (error) {
      throw Error(error)
    }
  }, // createCustomer

  async validateLegacyCustomer({ dispatch, commit }, credentials) {
    try {
      let { token, valid } = await CustomerService.validateLegacyAccount(
        credentials
      )
      commit(`auth/${Mtxs.SET_TOKEN}`, token, { root: true })
      dispatch('auth/setLaravelAuthHeaders', null, { root: true })
      return valid
    } catch (error) {
      return false
    }
  }, // validateLegacyCustomer

  async setLegacyPassword({ dispatch, commit, rootGetters }, password) {
    try {
      let shopId = rootGetters['shop/getShopId']
      let { data: cust } = await Laravel.post(
        `/shops/${shopId}/legacy-customer/`,
        {
          password,
        }
      )
      commit(Mtxs.SET_CUSTOMER, cust)
      dispatch(`auth/setUser`, { ...cust, is_logged_in: true }, { root: true })
      commit(`auth/${Mtxs.SET_TOKEN}`, cust.api_token, { root: true })
      dispatch('auth/setLaravelAuthHeaders', null, { root: true })

      return cust
    } catch (error) {
      return Promise.reject(error)
    }
  }, // setLegacyPassword

  async setLegacyEmailAndPassword(
    { dispatch, commit, rootGetters },
    { email, password }
  ) {
    if (!email || !password) {
      return Promise.reject(
        'You must provide both email and password to update the account.'
      )
    }
    try {
      let shopId = rootGetters['shop/getShopId']
      let { data: cust } = await Laravel.post(
        `/shops/${shopId}/legacy-customer/`,
        {
          email,
          password,
        }
      )
      dispatch(`auth/setUser`, { ...cust, is_logged_in: true }, { root: true })
      commit(`auth/${Mtxs.SET_TOKEN}`, cust.api_token, { root: true })
      dispatch('auth/setLaravelAuthHeaders', null, { root: true })
      return cust
    } catch (error) {
      return Promise.reject(error)
    }
  }, // setLegacyEmailAndPassword

  async updateCustomer({ rootGetters }, { customerId, data }) {
    try {
      customerId = customerId || rootGetters['auth/getAuthUserId']
      if (!customerId) {
        return false
      }
      await CustomerModel.updateModel(customerId, data)
    } catch (error) {
      throw Error(error)
    }
  }, // updateCustomer

  async updateSelf({ rootGetters }, { customerId, data }) {
    try {
      customerId = customerId || rootGetters['auth/getAuthUserId']
      if (!customerId) {
        return false
      }
      await CustomerModel.updateSelf(customerId, data)
    } catch (error) {
      throw Error(error)
    }
  }, // updateSelf

  async fetchOrders({ rootGetters }, customerId) {
    try {
      customerId = customerId || rootGetters['auth/getAuthUserId']

      if (!customerId) {
        return false
      }
      await OrderModel.fetchForCustomer(customerId)
    } catch (error) {
      throw Error(error)
    }
  }, // fetchOrders

  async saveCustomerAddress({ commit, getters }, { zip, street }) {
    try {
      let customerId = getters.getCustomerId
      let { data: cust } = await Laravel.post(`/customers/${customerId}`, {
        zip,
        street,
      })
      commit(Mtxs.SET_CUSTOMER, cust)
      return cust
    } catch (error) {
      throw Error(error)
    }
  }, // saveCustomerAddress

  setupFullStory({ getters }) {
    // Setup FullStory user tracking
    if (
      typeof window !== 'undefined' &&
      typeof window.FS !== 'undefined' &&
      getters.getCustomer
    ) {
      window.FS.identify(getters.getCustomer.id, {
        displayName: getters.getCustomer.fields.Name,
      })
    }
  }, // setupFullStory

  logout({ commit, dispatch }) {
    commit(Mtxs.SET_CUSTOMER, {})
    dispatch('snackbars/setLoggedOutSnackbar', true, { root: true })
  },

  setCustomerEmail({ commit }, email) {
    commit(Mtxs.UPDATE_CUSTOMER, { email })
  }, // setCustomerEmail
} // actions

const formatPhoneNumber = (phone) => {
  if (!phone.length) {
    return phone
  }
  const parsed = parsePhoneNumberFromString(phone, 'US')
  return typeof parsed !== 'undefined' ? parsed.formatNational() : ''
}
